import { ComboBox, FileUploaderDropContainer, FileUploaderItem, Stack, TextInput } from '@carbon/react';
import { asProblem, problemMessage, useReferenceOptions, type ReferenceOption } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { TransientNotice } from '../common/Notices';
import { IMAGE_MAX_BYTES, IMAGE_TYPES, imagesKey, uploadImage, type ImageAsset } from './imagesApi';

interface UploadItem {
  id: string;
  name: string;
  status: 'uploading' | 'complete' | 'edit';
  error?: string;
}

/**
 * Upload images by dragging them in or choosing them (Carbon file uploader, FR-ONL-17): each file shows its progress, and
 * a refused file shows the server's reason under its name. With `brandId` set the images go to that brand (`null`: every
 * brand) and no brand is asked for, as in a form's picker; left out, the brand is an optional choice.
 */
export function ImageUploader({
  brandId,
  multiple = true,
  headingLevel = 2,
  heading = true,
  onUploaded,
}: {
  brandId?: string | null;
  multiple?: boolean;
  /** 3 inside a dialog or under another section's h2. */
  headingLevel?: 2 | 3;
  /** False inside a dialog whose own heading already says it: only the hint shows. */
  heading?: boolean;
  onUploaded?(image: ImageAsset): void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const queryClient = useQueryClient();
  const brands = useReferenceOptions(brandId === undefined ? 'org.brand' : undefined);
  const [brand, setBrand] = useState<ReferenceOption | null>(null);
  const [altText, setAltText] = useState('');
  const [items, setItems] = useState<UploadItem[]>([]);
  const [notice, setNotice] = useState<string>();
  const Heading = `h${headingLevel}` as const;
  const busy = items.some((i) => i.status === 'uploading');

  const update = (itemId: string, change: Partial<UploadItem>) =>
    setItems((list) => list.map((i) => (i.id === itemId ? { ...i, ...change } : i)));

  const addFiles = async (files: (File & { invalidFileType?: boolean })[]) => {
    setNotice(undefined);
    const batch = files.map((file, index) => {
      const error = file.invalidFileType
        ? t('images.upload.invalidType')
        : file.size > IMAGE_MAX_BYTES
          ? t('images.upload.tooLarge')
          : undefined;
      return { file, item: { id: `${Date.now()}-${index}`, name: file.name, status: error ? 'edit' : 'uploading', error } as UploadItem };
    });
    setItems(batch.map((b) => b.item));
    let created = 0;
    let existing = 0;
    for (const { file, item } of batch) {
      if (item.error) continue;
      try {
        const result = await uploadImage(file, {
          brandId: brandId === undefined ? brand?.value : (brandId ?? undefined),
          altText,
        });
        update(item.id, { status: 'complete' });
        if (result.existing) existing += 1;
        else created += 1;
        onUploaded?.(result.image);
      } catch (error) {
        update(item.id, { status: 'edit', error: problemMessage(t, asProblem(error)) });
      }
    }
    if (created + existing > 0) {
      await queryClient.invalidateQueries({ queryKey: imagesKey });
      setNotice(
        [
          created > 0 ? t('images.upload.done', { count: created }) : '',
          existing > 0 ? t('images.upload.existing', { count: existing }) : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    }
  };

  return (
    <Stack gap={5} className="pos-images__upload">
      <div>
        {heading && <Heading className="pos-images__heading">{t('images.upload.title')}</Heading>}
        <p className="pos-images__muted">{t('images.upload.hint')}</p>
      </div>
      {brandId === undefined && (
        <ComboBox<ReferenceOption>
          id={`${id}-brand`}
          titleText={t('images.columns.brand')}
          placeholder={t('images.allBrands')}
          helperText={t('images.upload.brandHelp')}
          items={brands.data ?? []}
          itemToString={(o) => o?.label ?? ''}
          selectedItem={brand}
          onChange={({ selectedItem }) => setBrand(selectedItem ?? null)}
        />
      )}
      <TextInput
        id={`${id}-alt`}
        labelText={t('images.upload.altText')}
        helperText={t('images.upload.altTextHelp')}
        value={altText}
        maxLength={500}
        onChange={(e) => setAltText(e.target.value)}
      />
      <FileUploaderDropContainer
        id={`${id}-file`}
        labelText={t(multiple ? 'images.upload.drop' : 'images.upload.dropOne')}
        accept={IMAGE_TYPES}
        multiple={multiple}
        disabled={busy}
        onAddFiles={(_event, { addedFiles }) => void addFiles(addedFiles)}
      />
      {items.length > 0 && (
        <div className="pos-images__files">
          {items.map((item) => (
            <FileUploaderItem
              key={item.id}
              uuid={item.id}
              name={item.name}
              status={item.status}
              invalid={Boolean(item.error)}
              errorSubject={item.error}
              iconDescription={
                item.status === 'uploading'
                  ? t('images.upload.uploading')
                  : item.status === 'complete'
                    ? t('images.upload.uploaded')
                    : t('images.upload.remove', { name: item.name })
              }
              onDelete={() => setItems((list) => list.filter((i) => i.id !== item.id))}
            />
          ))}
        </div>
      )}
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
    </Stack>
  );
}
